import { type ReactNode } from "react"
import { ChevronRight, Home } from "lucide-react"
import { cn } from "@/lib/utils"

// Crumbs whose text comes from user data (bid title, artifact name, section
// title) must truncate — otherwise a long title wraps and blows out the h-12
// header row. Fixed-word crumbs ("Bids", "Responses") never shrink.
const CRUMB_LINK = "text-muted-foreground hover:text-foreground transition-colors"
const CRUMB_DYNAMIC = "truncate max-w-[100px] lg:max-w-[180px] xl:max-w-[260px]"

export function Breadcrumb({ children }: { children: ReactNode }) {
  return (
    <nav
      aria-label="Breadcrumb"
      className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden whitespace-nowrap text-sm"
    >
      {children}
    </nav>
  )
}

export function BreadcrumbSeparator() {
  return <ChevronRight className="h-3.5 w-3.5 shrink-0 text-subtle" aria-hidden="true" />
}

export function BreadcrumbHome({ onClick }: { onClick: () => void }) {
  return (
    <button onClick={onClick} className={cn(CRUMB_LINK, "shrink-0")} aria-label="Home">
      <Home className="h-4 w-4" />
    </button>
  )
}

export function BreadcrumbLink({
  label,
  onClick,
  dynamic,
}: {
  label: string
  onClick: () => void
  dynamic?: boolean
}) {
  return (
    <button
      onClick={onClick}
      title={dynamic ? label : undefined}
      className={cn(CRUMB_LINK, dynamic ? CRUMB_DYNAMIC : "shrink-0")}
    >
      {label}
    </button>
  )
}

// The current page is the last crumb: no fixed cap, it takes whatever width is
// left over and truncates when there isn't enough.
export function BreadcrumbCurrent({
  label,
  dynamic,
  className,
}: {
  label: string
  dynamic?: boolean
  className?: string
}) {
  return (
    <span
      aria-current="page"
      title={dynamic ? label : undefined}
      className={cn("text-foreground", dynamic ? "min-w-0 truncate" : "shrink-0", className)}
    >
      {label}
    </span>
  )
}
